import { fg } from '@confluence/feature-gating';

import type { SmartLinkType } from './getSmartLinks.ts';
import { RESOLVED, FORBIDDEN } from './status.ts';

/**
 * Partitions smart links into those resolved by GraphQL (Confluence-only)
 * and those that still need to be resolved via ORS.
 */
export const partitionResolvedLinks = (
	links: SmartLinkType[],
): { resolvedFromGql: SmartLinkType[]; linksForORS: SmartLinkType[] } => {
	const resolvedFromGql: SmartLinkType[] = [];
	const linksForORS: SmartLinkType[] = [];

	for (const link of links) {
		if (
			link.product === 'confluence' &&
			link.resolved === true &&
			(!fg('cc_preserve_smart_link_anchor_content') || !hasUrlFragment(link.resourceUrl))
		) {
			resolvedFromGql.push(link);
		} else {
			linksForORS.push(link);
		}
	}

	return { resolvedFromGql, linksForORS };
};

export function hasUrlFragment(url?: string | null): boolean {
	if (!url) {
		return false;
	}

	try {
		return new URL(url).hash.length > 1;
	} catch {
		const [, fragment] = url.split('#');
		return Boolean(fragment);
	}
}

/**
 * Converts Confluence SmartLinks that were resolved via GraphQL directly into the
 * SmartCard Provider initial state format, matching the shape produced by
 * getInitialState (which processes ORS responses).
 *
 * This allows resolved Confluence links to bypass the ORS call entirely while still
 * producing a compatible state shape for the SmartCard Provider stored
 * in window.__SSR_SMARTLINKS__.
 *
 * NOTE: This is currently only supported for Confluence links. Non-Confluence links
 * should continue to be resolved via ORS.
 *
 * The shape produced is:
 *   { [url]: { status: string, details: { meta: {...}, data: { url, name, ... } } } }
 */
export const buildResolvedSmartLinkState = (
	resolvedLinks: SmartLinkType[],
): Record<string, any> => {
	if (!resolvedLinks?.length) {
		return {};
	}

	return resolvedLinks.reduce(
		(map, link) => {
			const url = link.resourceUrl;
			if (!url) {
				return map;
			}

			const iconUrl = getStaticIconUrl(link.resourceType, url) ?? link.iconUrl;
			const cardState = {
				status: link.canView === true ? RESOLVED : FORBIDDEN,
				details: {
					meta: {
						access: link.canView === true ? 'granted' : 'forbidden',
						auth: [],
						definitionId: getDefinitionId(link.resourceType),
						resourceType: link.resourceType,
						product: 'confluence',
					},
					data: {
						'@context': {
							'@vocab': 'https://www.w3.org/ns/activitystreams#',
							atlassian: 'https://schema.atlassian.com/ns/vocabulary#',
							schema: 'https://schema.org/',
						},
						'@type': getResourceType(link.resourceType, link.subType),
						name: link.title || '',
						url,
						...(hasTopLevelIcon(link.resourceType) && iconUrl
							? { icon: { '@type': 'Image', url: iconUrl } }
							: {}),
						...(hasTitlePrefix(link.resourceType) && link.emojiTitle
							? {
									'atlassian:titlePrefix': {
										'@type': 'atlassian:Emoji',
										text: link.emojiTitle,
									},
								}
							: {}),
						generator: {
							'@type': 'Application',
							'@id': getGeneratorId(link.resourceType),
							name: getGeneratorName(link.resourceType),
							...(iconUrl ? { icon: { '@type': 'Image', url: iconUrl } } : {}),
						},
					},
				},
			};

			return {
				...map,
				[url]: cardState,
			};
		},
		{} as Record<string, any>,
	);
};

/**
 * Map resourceType to the ORS definitionId.
 * Whiteboards and databases use a different provider than pages/blogs/spaces.
 */
function getDefinitionId(resourceType?: string | null): string {
	switch (resourceType) {
		case 'whiteboard':
		case 'database':
		case 'slide':
			return 'canvas-native-object-provider';
		default:
			return 'integrations-confluence-object-provider';
	}
}

/**
 * Whether this resourceType has a top-level `icon` field in the ORS data response.
 * Pages and blogs do not; whiteboards, databases, slides, folders, and spaces do.
 */
function hasTopLevelIcon(resourceType?: string | null): boolean {
	switch (resourceType) {
		case 'whiteboard':
		case 'database':
		case 'slide':
		case 'folder':
		case 'space':
			return true;
		default:
			return false;
	}
}

/**
 * Returns a hardcoded icon URL for resource types that do not have an icon URL returned by the
 * GraphQL API. Whiteboards and databases fall into this category.
 * These URL must match those listed in https://bitbucket.org/atlassian/canvas/src/68e87076d0168bb32184f72e66cea5cfc0a96516/services/canvas-server/canvas-server/src/routes/private/smart-link/smart-link-utils.ts
 * For database links with an `entryId` query parameter, a database-entry icon is used instead.
 */
function getStaticIconUrl(resourceType?: string | null, url?: string | null): string | null {
	switch (resourceType) {
		case 'whiteboard':
			return 'https://canvas-frontend.prod-east.frontend.public.atl-paas.net/assets/whiteboard-logo.svg';
		case 'database': {
			if (url) {
				try {
					const parsedUrl = new URL(url);
					if (parsedUrl.searchParams.has('entryId')) {
						return 'https://canvas-frontend.prod-east.frontend.public.atl-paas.net/assets/database-entry-logo.svg';
					}
				} catch {
					// If URL parsing fails, fall through to the default database icon
				}
			}
			return 'https://canvas-frontend.prod-east.frontend.public.atl-paas.net/assets/database-logo.svg';
		}
		case 'slide':
			return 'https://canvas-frontend.prod-east.frontend.public.atl-paas.net/assets/slides-logo.svg';
		case 'folder':
			return 'https://dam-cdn.atl.orangelogic.com/CDNLink/AT1315J.svg';
		default:
			return null;
	}
}

/**
 * Whether this resourceType includes `atlassian:titlePrefix` in the ORS data response.
 * Pages, blogs, and spaces do; whiteboards and databases do not.
 */
function hasTitlePrefix(resourceType?: string | null): boolean {
	switch (resourceType) {
		case 'whiteboard':
		case 'database':
		case 'slide':
			return false;
		default:
			return true;
	}
}

/**
 * Map resourceType to the generator @id used by the SmartCard Provider.
 * Values are based on actual ORS responses.
 */
function getGeneratorId(resourceType?: string | null): string {
	switch (resourceType) {
		case 'whiteboard':
			return 'https://www.atlassian.com/#ConfluenceWhiteboards';
		case 'database':
			return 'https://www.atlassian.com/#ConfluenceDatabases';
		case 'slide':
			return 'https://www.atlassian.com/#ConfluenceSlides';
		default:
			return 'https://www.atlassian.com/#Confluence';
	}
}

/**
 * Map resourceType to the generator display name.
 * Values are based on actual ORS responses.
 */
function getGeneratorName(resourceType?: string | null): string {
	switch (resourceType) {
		case 'whiteboard':
			return 'Confluence Whiteboards';
		case 'database':
			return 'Confluence Databases';
		case 'slide':
			return 'Confluence Slides';
		default:
			return 'Confluence';
	}
}

/**
 * Map resourceType to an ActivityStreams @type value for Confluence content.
 * Values are based on actual ORS responses.
 */
function getResourceType(resourceType?: string | null, subType?: string | null): string | string[] {
	switch (resourceType) {
		case 'blogpost':
			return ['Document', 'schema:BlogPosting'];
		case 'page':
			if (subType === 'live') {
				return ['Document', 'schema:DigitalDocument'];
			}
			return ['Document', 'schema:TextDigitalDocument'];
		case 'whiteboard':
		case 'database':
		case 'slide':
			return 'Document';
		case 'space':
			return ['Object', 'atlassian:Project'];
		default:
			return ['Document', 'schema:TextDigitalDocument'];
	}
}
